import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import TextInput from '../../../components/ui/TextInput';
import { searchPositions } from '../orgChartApi';
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_LENGTH, SEARCH_PAGE_SIZE, searchPathLabel } from '../orgChartModel';

// Spec §5.8 — tuvalin sol üstünde arama: en az 2 karakter, 300 ms gecikme, 20'lik sayfalar (kaydırınca sonraki),
// ↑/↓/Enter/Esc. Seçim kutuyu temizler; odaklama ve vurgu sayfanın işidir (onSelect).

// `inline` (spec 2026-10-24 §4): araç çubuğu akışında durur, sonuç listesi kutunun altında yüzer.
const Wrap = styled.div`
  z-index: 30;
  width: 320px;
  ${({ $inline }) => ($inline
    ? 'position: relative; flex: 0 1 320px; min-width: 200px;'
    : 'position: absolute; top: 12px; left: 12px; max-width: calc(100% - 24px);')}
`;

const List = styled.div`
  ${({ $inline }) => ($inline ? 'position: absolute; top: 100%; left: 0; right: 0;' : '')}
  margin-top: 4px;
  max-height: 360px;
  overflow-y: auto;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 66, 66, 0.15);
  padding: 4px;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.1)' : 'transparent')};
  &:hover { background: rgba(2, 145, 239, 0.08); }
`;

const Texts = styled.div`
  min-width: 0;
  flex: 1;
`;

const Name = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #004242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  font-size: 12px;
  color: #6B7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PathText = styled(Meta)`
  font-size: 11px;
  color: #A0A8B4;
`;

const Empty = styled.div`
  padding: 8px;
  font-size: 13px;
  color: #8C96A0;
`;

const CLOSED = { open: false, items: [], total: 0, page: 1 };

export default function OrgSearch({ onSelect, edit = false, inline = false }) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [result, setResult] = useState(CLOSED);
  const [active, setActive] = useState(-1);
  const requestId = useRef(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < SEARCH_MIN_LENGTH) {
      requestId.current += 1;          // uçuştaki yanıt artık geçersiz
      setResult(CLOSED);
      setActive(-1);
      return undefined;
    }
    const timer = setTimeout(async () => {
      const id = ++requestId.current;
      const res = await searchPositions({ q, page: 1, pageSize: SEARCH_PAGE_SIZE, edit });
      if (id !== requestId.current) return;   // daha yeni bir arama yola çıktı
      setResult({
        open: true,
        items: res.state === 'ok' ? res.data.items : [],
        total: res.state === 'ok' ? res.data.totalCount : 0,
        page: 1,
      });
      setActive(-1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term, edit]);

  useEffect(() => {
    if (!result.open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setResult((prev) => ({ ...prev, open: false }));
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [result.open]);

  const loadMore = useCallback(async () => {
    if (result.items.length >= result.total) return;
    const id = ++requestId.current;
    const res = await searchPositions({ q: term.trim(), page: result.page + 1, pageSize: SEARCH_PAGE_SIZE, edit });
    if (id !== requestId.current || res.state !== 'ok') return;
    setResult((prev) => ({ ...prev, items: prev.items.concat(res.data.items), page: prev.page + 1 }));
  }, [result, term, edit]);

  const choose = (item) => {
    requestId.current += 1;
    setTerm('');
    setResult(CLOSED);
    setActive(-1);
    onSelect(item.positionId);
  };

  const onKeyDown = (e) => {
    if (!result.open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(result.items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = result.items[active] || result.items[0];
      if (item) choose(item);
    } else if (e.key === 'Escape') {
      setResult((prev) => ({ ...prev, open: false }));
      setActive(-1);
    }
  };

  return (
    <Wrap ref={wrapRef} data-testid="org-search" data-inline={inline ? 'true' : undefined} $inline={inline}>
      <TextInput
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('orgChart.searchPlaceholder')}
        aria-label={t('orgChart.searchPlaceholder')}
        data-testid="org-search-input"
      />
      {result.open && (
        <List
          $inline={inline}
          role="listbox"
          data-testid="org-search-results"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) loadMore();
          }}
        >
          {result.items.length === 0 && <Empty>{t('orgChart.searchNoResults')}</Empty>}
          {result.items.map((item, i) => (
            <Option
              key={item.positionId}
              role="option"
              aria-selected={i === active}
              $active={i === active}
              data-testid={`org-search-item-${item.positionId}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(item)}
            >
              <Avatar src={item.occupantPhoto || undefined} sx={{ width: 28, height: 28, fontSize: 12 }}>
                {item.userName ? item.userName.charAt(0) : '-'}
              </Avatar>
              <Texts>
                <Name>{item.userName || t('orgChart.vacantPosition')}</Name>
                <Meta>{[item.titleName, item.departmentName].filter(Boolean).join(' · ') || '-'}</Meta>
                {item.path?.length > 0 && <PathText>{searchPathLabel(item.path)}</PathText>}
              </Texts>
            </Option>
          ))}
        </List>
      )}
    </Wrap>
  );
}
